import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { EnvelopeIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { API_URLS } from '../config/api';

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [apiUrl, setApiUrl] = useState('');

  // Determine the API URL on component mount
  useEffect(() => {
    const hostname = window.location.hostname;
    let baseUrl;
    
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      baseUrl = API_URLS.default;
    } else {
      baseUrl = API_URLS.default;
    }
    
    console.log('Using API URL:', baseUrl);
    setApiUrl(baseUrl);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    if (!apiUrl) {
      setError('API URL not configured. Please try again.');
      setLoading(false);
      return;
    }

    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${apiUrl}/api/auth/forgot-password`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.timeout = 15000; // 15 seconds timeout
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            setMessage(data.message);
            setSubmitted(true);
          } catch (parseError) {
            console.error('Error parsing response:', parseError);
            setError('Invalid response from server');
          }
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            setError(errorData.message || 'Failed to send reset email');
          } catch (parseError) {
            setError(`Server error: ${xhr.status}`);
          }
        }
        setLoading(false);
      };
      
      xhr.onerror = function() {
        console.error('XHR error:', xhr.statusText);
        setError('Network error. Please check your connection and try again.');
        setLoading(false);
      };
      
      xhr.ontimeout = function() {
        setError('Request timed out. Please try again.');
        setLoading(false);
      };
      
      xhr.send(JSON.stringify({ email }));
    } catch (err) {
      console.error('Forgot password error:', err);
      setError(err.message || 'An unexpected error occurred');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setEmail(e.target.value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500">
      <div className="container mx-auto px-4 h-full">
        <div className="flex content-center items-center justify-center min-h-screen py-12">
          <div className="w-full lg:w-5/12 px-4">
            <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-2xl bg-white border-0">
              <div className="rounded-t-2xl mb-0 px-8 pt-8">
                <div className="text-center mb-3">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    Forgot Password?
                  </h1>
                  <p className="text-gray-600 text-sm mb-4">
                    {submitted 
                      ? "Check your email for reset instructions" 
                      : "Enter your email address and we'll send you a link to reset your password"
                    }
                  </p>
                </div>
              </div>

              <div className="flex-auto px-8 pb-8 pt-0">
                {error && (
                  <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-center">
                    {error}
                  </div>
                )}

                {message && (
                  <div className="mb-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-xl text-center">
                    {message}
                  </div>
                )}

                {!submitted ? (
                  <form className="space-y-5" onSubmit={handleSubmit}>
                    <div className="relative">
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email address
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <EnvelopeIcon className="h-5 w-5 text-indigo-500" />
                        </div>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          inputMode="email"
                          autoComplete="email"
                          required
                          className="appearance-none block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          value={email}
                          onChange={handleChange}
                          placeholder="john.doe@example.com"
                        />
                      </div>
                    </div>

                    <div className="text-center mt-6">
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-all duration-200"
                      >
                        {loading ? (
                          <div className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Sending email...
                          </div>
                        ) : (
                          'Send Reset Link'
                        )}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="text-center space-y-4">
                    <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100">
                      <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-lg font-medium text-gray-900">Email Sent!</h3>
                      <p className="text-sm text-gray-600">
                        We've sent a password reset link to <strong>{email}</strong>.
                        Please check your email and follow the instructions to reset your password.
                      </p>
                      <p className="text-xs text-gray-500">
                        The reset link will expire in 1 hour for security reasons.
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setSubmitted(false);
                        setMessage('');
                        setEmail('');
                      }}
                      className="text-indigo-600 hover:text-indigo-500 text-sm font-medium"
                    >
                      Send another email
                    </button>
                  </div>
                )}

                <div className="text-center mt-6">
                  <Link 
                    to="/login" 
                    className="inline-flex items-center text-sm text-gray-600 hover:text-indigo-600 font-medium"
                  >
                    <ArrowLeftIcon className="h-4 w-4 mr-1" />
                    Back to login
                  </Link>
                </div>

                {!submitted && (
                  <div className="mt-6">
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white text-gray-500">
                          Test Account Recovery
                        </span>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-2">
                      <button
                        type="button"
                        onClick={() => setEmail('rider@test.com')}
                        disabled={loading}
                        className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Use Test Rider Email
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword; 